import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FaFilePdf } from 'react-icons/fa';
import API_URL from '../../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../../utils/contentSync';
import './Certificates.css';

const PDF_FILE_PATTERN = /\.pdf(?:$|[?#])/i;

const CertificatesHeadingContent = () => (
  <>
    <h1 className="certificates-page-title">Certificates</h1>
    <div className="certificates-heading-line" />
  </>
);

const isPdfFile = (url = '') => PDF_FILE_PATTERN.test(url);

const normalizeCertificateUrl = (pathOrUrl) => {
  if (!pathOrUrl) return '';

  const source = String(pathOrUrl).trim().replace(/\\/g, '/');
  if (!source) return '';

  if (/^https?:\/\//i.test(source)) {
    return source;
  }

  const assetOrigin = (API_URL.ORIGIN || API_URL.BASE.replace(/\/api\/?$/, '')).replace(/\/$/, '');
  const trimmedPath = source.replace(/^\/+/, '');

  if (
    trimmedPath.startsWith('storage/') ||
    trimmedPath.startsWith('uploads/') ||
    trimmedPath.startsWith('api/storage/') ||
    trimmedPath.startsWith('api/uploads/')
  ) {
    return `${assetOrigin}/${trimmedPath}`;
  }

  const storageIndex = trimmedPath.indexOf('storage/');
  if (storageIndex !== -1) {
    return `${assetOrigin}/${trimmedPath.slice(storageIndex)}`;
  }

  const uploadsIndex = trimmedPath.indexOf('uploads/');
  if (uploadsIndex !== -1) {
    return `${assetOrigin}/${trimmedPath.slice(uploadsIndex)}`;
  }

  return `${assetOrigin}/storage/certificates/${trimmedPath}`;
};

const buildCertificateMedia = (certificate) => {
  const urls = [
    normalizeCertificateUrl(certificate.imageUrl),
    normalizeCertificateUrl(certificate.image),
  ].filter((url, index, allUrls) => url && allUrls.indexOf(url) === index);

  const fileUrl = urls[0] || '';
  const previewUrl = urls.find((url) => !isPdfFile(url)) || fileUrl;
  const imageFallbackUrls = urls.filter((url) => !isPdfFile(url) && url !== previewUrl);

  return {
    fileUrl,
    previewUrl,
    imageFallbackUrls,
  };
};

const PdfCertificatePreview = ({ url, name }) => {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let isActive = true;
    let loadingTask = null;
    let renderTask = null;
    let pdfDocument = null;

    const renderPreview = async () => {
      try {
        setStatus('loading');

        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        if (!globalThis.pdfjsWorker?.WorkerMessageHandler) {
          await import('pdfjs-dist/legacy/build/pdf.worker.mjs');
        }

        loadingTask = pdfjs.getDocument(url);
        pdfDocument = await loadingTask.promise;

        const canvas = canvasRef.current;

        if (!canvas || !isActive) {
          return;
        }

        const page = await pdfDocument.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth = canvas.parentElement?.clientWidth || 320;
        const devicePixelRatio =
          typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 1.5) : 1;
        const scale = Math.max(1, Math.min(1.35, (containerWidth / baseViewport.width) * devicePixelRatio));
        const viewport = page.getViewport({ scale });

        const context =
          canvas.getContext('2d', { alpha: false }) ||
          canvas.getContext('2d');

        if (!context) {
          throw new Error('Canvas 2D context is not available.');
        }
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        renderTask = page.render({
          canvasContext: context,
          viewport,
        });

        await renderTask.promise;
        page.cleanup();

        if (isActive) {
          setStatus('ready');
        }
      } catch (previewError) {
        if (isActive) {
          console.error('Failed to render PDF certificate preview:', previewError);
          setStatus('error');
        }
      }
    };

    renderPreview();

    return () => {
      isActive = false;

      try {
        renderTask?.cancel();
      } catch (_) {}

      try {
        loadingTask?.destroy();
      } catch (_) {}

      try {
        pdfDocument?.destroy();
      } catch (_) {}
    };
  }, [url]);

  if (status === 'error') {
    return (
      <object
        data={`${url}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
        type="application/pdf"
        className="certificate-pdf-object"
        aria-label={`${name} certificate preview`}
      >
        <div className="certificate-pdf-fallback">
          <FaFilePdf className="certificate-pdf-icon" />
          <p>Open certificate</p>
          <small>Preview is not supported in this browser</small>
        </div>
      </object>
    );
  }

  return (
    <div className="certificate-pdf-preview">
      {status === 'loading' && (
        <div className="certificate-pdf-loading">
          <div className="spinner"></div>
          <p>Loading preview...</p>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="certificate-pdf-canvas"
        aria-label={`${name} certificate preview`}
      />
    </div>
  );
};

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());

  const fetchCertificates = async () => {
    try {
      setLoading((current) => current && certificates.length === 0);

      const response = await fetch(buildFreshUrl(API_URL.CERTIFICATES), {
        cache: 'no-store',
        headers: {
          ...NO_CACHE_JSON_HEADERS,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch certificates');
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        const processedCertificates = data.data.map((cert) => ({
          ...cert,
          imageUrl: normalizeCertificateUrl(cert.imageUrl) || normalizeCertificateUrl(cert.image),
        }));

        setCertificates(processedCertificates);
      } else {
        setCertificates([]);
      }

      setImageErrors(new Set());
      setError(null);
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setError(err.message);
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [CONTENT_SYNC_KEYS.CERTIFICATES],
        onUpdate: fetchCertificates,
      }),
    []
  );

  if (loading) {
    return (
      <div className="certificates-page">
        <div className="certificates-container">
          <div className="certificates-header">
            <CertificatesHeadingContent />
          </div>
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading certificates...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="certificates-page">
        <div className="certificates-container">
          <div className="certificates-header">
            <CertificatesHeadingContent />
          </div>
          <div className="error-state">
            <p>Error loading certificates: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <div className="certificates-page">
        <div className="certificates-container">
          <div className="certificates-header">
            <CertificatesHeadingContent />
          </div>
          <div className="empty-state">
            <p>No certificates available at the moment.</p>
            <p>Please add certificates through the admin panel.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="certificates-page">
      <div className="certificates-container">
        <motion.div
          className="certificates-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <CertificatesHeadingContent />
        </motion.div>

        <motion.div
          className="certificate-display"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="certificates-row">
            {certificates.map((cert, index) => {
              const certKey = cert._id || cert.id || cert.name;
              const { fileUrl, previewUrl, imageFallbackUrls } = buildCertificateMedia(cert);
              const showImage = previewUrl && !isPdfFile(previewUrl) && !imageErrors.has(certKey);

              return (
                <motion.div
                  key={certKey}
                  className="certificate-item"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div
                    className="certificate-card"
                    onClick={() => {
                      if (fileUrl) {
                        window.open(fileUrl, '_blank');
                      }
                    }}
                  >
                    <div className="certificate-image">
                      {showImage ? (
                        <img
                          src={previewUrl}
                          alt={`${cert.name} certificate preview`}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const attemptedUrls = new Set(
                              (e.currentTarget.dataset.attemptedUrls || '')
                                .split('|')
                                .filter(Boolean)
                            );
                            attemptedUrls.add(e.currentTarget.currentSrc || e.currentTarget.src);

                            const nextFallbackUrl = imageFallbackUrls.find(
                              (url) => !attemptedUrls.has(url)
                            );

                            if (nextFallbackUrl) {
                              e.currentTarget.dataset.attemptedUrls = Array.from(attemptedUrls).join('|');
                              e.currentTarget.src = nextFallbackUrl;
                              return;
                            }

                            setImageErrors((prev) => new Set(prev).add(certKey));
                          }}
                        />
                      ) : fileUrl && isPdfFile(fileUrl) ? (
                        <PdfCertificatePreview
                          url={fileUrl}
                          name={cert.name}
                        />
                      ) : (
                        <div className="image-placeholder">
                          <p>Image not available</p>
                          <small>Click to view certificate</small>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="certificate-title">
                    <h3>{cert.name}</h3>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Certificates;
